// Basic Calculator Comprehensive Tutorial Guide
// Detailed, feature-specific tutorial for the Basic Retirement Calculator
// Last Updated: October 2026

import React, { useState, useEffect } from 'react';
import { getAgePensionHelpFacts } from './utils/statutoryHelpCopy.js';

const fmtAud = (n, decimals = 0) =>
    `$${Number(n).toLocaleString('en-AU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;

export const BasicTutorialGuide = () => {
    const [activeStep, setActiveStep] = useState(0);
    const tutorialRef = React.useRef(null);
    const pension = getAgePensionHelpFacts();
    const incomeFreeSingleFn = pension.incomeTest.SINGLE.FREE_THRESHOLD / 26;
    const incomeFreeCoupleFn = pension.incomeTest.COUPLE.FREE_THRESHOLD / 26;
    const at = pension.assetTest;

    // Auto-scroll to top of tutorial when step changes
    useEffect(() => {
        if (tutorialRef.current) {
            tutorialRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [activeStep]);

    const tutorialSteps = [
        {
            title: "Welcome to the Basic Retirement Calculator",
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700">
                        The Basic Retirement Calculator provides a quick snapshot of your retirement finances.
                        It calculates how much income you can sustainably draw from your superannuation, factoring 
                        in investment returns, inflation, Age Pension entitlements, and other income sources.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">What This Calculator Does:</h4>
                        <ul className="list-disc list-inside text-blue-700 space-y-1">
                            <li>Calculates your <strong>Maximum Sustainable Income</strong> - the highest annual income you can withdraw without running out of money</li>
                            <li>Projects your super balance over retirement showing year-by-year breakdown</li>
                            <li>Estimates Age Pension entitlement using current Centrelink thresholds</li>
                            <li>Factors in superannuation fees (admin, investment, insurance)</li>
                            <li>Shows income composition: Super withdrawals + Age Pension + Other Income</li>
                            <li>Provides what-if tools: Live to Age, More Income, Annual Fees, Sequence Risk (plus Surviving Spouse on desktop for couples)</li>
                        </ul>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">Who Should Use This:</h4>
                        <ul className="list-disc list-inside text-yellow-700 space-y-1">
                            <li>Anyone wanting a quick retirement estimate</li>
                            <li>Pre-retirees planning their retirement income</li>
                            <li>Those comparing different retirement scenarios</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Personal & Super Details",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Input Fields Explained:</h4>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📅 Your Age (Current)</h5>
                        <p className="text-sm text-gray-700 mb-2">Your age today. Used to calculate:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Years until retirement</li>
                            <li>Retirement period length (25 years by default, adjustable in the calculator)</li>
                            <li>Age Pension eligibility (pension age varies by birth year)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-100 text-xs text-blue-800 rounded">
                            <strong>Example:</strong> If you're 62 and retire at 67, the calculator can model the default 25-year horizon or any period you enter
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💼 Retirement Age</h5>
                        <p className="text-sm text-gray-700 mb-2">When you plan to stop working. Important because:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>You can access super from your <strong>preservation age</strong> (55-60 depending on birth year)</li>
                            <li>Age Pension available from <strong>pension age</strong> (67 for most people)</li>
                            <li>Retiring earlier = longer retirement = more savings needed</li>
                            <li>Retiring later = more time to save + shorter retirement period</li>
                        </ul>
                        <div className="mt-2 p-2 bg-yellow-100 text-xs text-yellow-800 rounded">
                            <strong>Tip:</strong> Delaying retirement by even 2-3 years can dramatically improve retirement security
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💰 Your Current Super Balance</h5>
                        <p className="text-sm text-gray-700 mb-2">Total superannuation balance today (single or couple combined).</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Check your super fund statement or myGov account</li>
                            <li>For couples: add both partners' balances together</li>
                            <li>This is the starting point for all calculations</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💵 Partner's Super Balance (if applicable)</h5>
                        <p className="text-sm text-gray-700 mb-2">Your partner's super balance. Affects:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Total household super assets</li>
                            <li>Age Pension calculations (couples have different thresholds)</li>
                            <li>Relationship status selection changes pension/asset test thresholds</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Income & Assets",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Income and Asset Inputs:</h4>
                    
                        <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">🏠 Homeownership Status</h5>
                        <p className="text-sm text-gray-700 mb-2">Critical for Age Pension calculation!</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li><strong>Homeowner:</strong> Lower asset test thresholds (your home is exempt from assets test)</li>
                            <li><strong>Non-homeowner:</strong> Higher thresholds (recognizes you need more assets)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-green-100 text-xs text-green-800 rounded">
                            <strong>Asset free areas / cut-offs ({pension.assetEffectiveLabel}, single):</strong><br/>
                            Homeowner: {fmtAud(at.SINGLE_HOMEOWNER.FREE_THRESHOLD)} – {fmtAud(at.SINGLE_HOMEOWNER.CUT_OFF)}<br/>
                            Non-homeowner: {fmtAud(at.SINGLE_NON_HOMEOWNER.FREE_THRESHOLD)} – {fmtAud(at.SINGLE_NON_HOMEOWNER.CUT_OFF)}
                        </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💼 Other Taxable Income (Annual)</h5>
                        <p className="text-sm text-gray-700 mb-2">Income from sources like:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Part-time work income</li>
                            <li>Rental property income (net of expenses)</li>
                            <li>Investment income (dividends, interest)</li>
                            <li>Business income</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-100 text-xs text-blue-800 rounded">
                            <strong>Important:</strong> Calculator applies tax to this income. Higher income = less Age Pension eligibility
                        </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💸 Other Non-Taxable Income</h5>
                        <p className="text-sm text-gray-700 mb-2">Tax-free income sources:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Income from super pensions (tax-free over 60)</li>
                            <li>Compensation payments</li>
                            <li>Certain government payments</li>
                        </ul>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">🏦 Assessable Assets</h5>
                        <p className="text-sm text-gray-700 mb-2">Assets counted in Age Pension assets test:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Investment properties</li>
                            <li>Cash savings and term deposits</li>
                            <li>Shares and managed funds outside super</li>
                            <li>Cars, boats, caravans (if valuable)</li>
                            <li><strong>Exclude:</strong> Your home, personal effects under $10,000</li>
                        </ul>
                        <div className="mt-2 p-2 bg-yellow-100 text-xs text-yellow-800 rounded">
                            <strong>Tip:</strong> Don't include super balance here - it's tracked separately
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Investment Assumptions",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">How Your Money Grows:</h4>
                    
                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📈 Assumed Investment Return (Annual %)</h5>
                        <p className="text-sm text-green-700 mb-2">Expected annual return before inflation and fees. Guidelines:</p>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                            <div className="bg-green-100 p-2 rounded">
                                <div className="font-semibold text-xs text-green-900">Conservative</div>
                                <div className="text-lg font-bold text-green-800">4-5%</div>
                                <div className="text-xs text-green-700">High cash/bonds</div>
                            </div>
                            <div className="bg-green-100 p-2 rounded">
                                <div className="font-semibold text-xs text-green-900">Balanced</div>
                                <div className="text-lg font-bold text-green-800">6-7%</div>
                                <div className="text-xs text-green-700">Mix of assets</div>
                            </div>
                            <div className="bg-green-100 p-2 rounded">
                                <div className="font-semibold text-xs text-green-900">Growth</div>
                                <div className="text-lg font-bold text-green-800">7-9%</div>
                                <div className="text-xs text-green-700">High shares</div>
                            </div>
                        </div>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Reality Check:</strong> Long-term Australian super fund averages: ~7-8% for balanced, ~8-9% for growth. But past returns don't guarantee future performance!
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📉 Assumed Inflation (Annual %)</h5>
                        <p className="text-sm text-blue-700 mb-2">Expected inflation rate - how much purchasing power decreases each year.</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li>RBA target: 2-3% (historical average ~2.5%)</li>
                            <li>Calculator shows all values in "today's dollars" by adjusting for inflation</li>
                            <li>Pension is indexed to inflation automatically</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Example:</strong> If you need $60,000 today, in 20 years you'll need $98,000 at 2.5% inflation to buy the same goods/services
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Superannuation Fees",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Understanding Fee Impact:</h4>
                    
                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">⚠️ Why Fees Matter</h5>
                        <p className="text-sm text-red-700 mb-2">Fees compound over time and can dramatically reduce retirement savings:</p>
                        <div className="bg-red-100 p-3 rounded mt-2">
                            <div className="text-sm text-red-900 font-semibold mb-1">Example: $500,000 super over 20 years at 7% return:</div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>With 0.5% fees: <span className="font-bold">$1.76M</span></div>
                                <div>With 1.5% fees: <span className="font-bold">$1.46M</span></div>
                            </div>
                            <div className="text-xs text-red-800 mt-1 font-semibold">Difference: $300,000 lost to higher fees!</div>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💳 Administration Fees ($ per year)</h5>
                        <p className="text-sm text-gray-700 mb-2">Flat dollar fee for account maintenance:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Industry funds: $50-$150/year</li>
                            <li>Retail funds: $100-$300/year</li>
                            <li>SMSFs: $1,000-$3,000/year (total fund cost)</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💳 Admin Fee (%)</h5>
                        <p className="text-sm text-gray-700 mb-2">Percentage admin fee charged on balance (separate from the flat $ fee):</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Often shown on your fee disclosure alongside investment fees</li>
                            <li>Default in the calculator is typically around 0.10%</li>
                            <li>Combines with the dollar admin fee each year</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📈 Pension Indexation Rate (%)</h5>
                        <p className="text-sm text-gray-700 mb-2">One annual real (above-CPI) growth rate for future Age Pension projections:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Grows maximum rates, part-pension cut-offs, supplements and rent-assistance caps</li>
                            <li>Asset/income free areas and deeming thresholds stay CPI-flat in today&apos;s dollars; deeming <em>percentages</em> stay fixed</li>
                            <li>Default 0.3% is a conservative single-track alternative to ASIC&apos;s separate CPI/wage paths</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📊 Investment Fees (% of balance)</h5>
                        <p className="text-sm text-gray-700 mb-2">Percentage fee on your balance:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Low-cost index funds: 0.1-0.3%</li>
                            <li>Industry funds (balanced): 0.5-1.0%</li>
                            <li>Retail funds: 0.8-1.5%</li>
                            <li>Active/specialty options: 1.0-2.0%+</li>
                        </ul>
                        <div className="mt-2 p-2 bg-orange-100 text-xs text-orange-800 rounded">
                            <strong>Check your fees:</strong> Log into myGov → Super → Fee disclosure. Many people overpay!
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">🏥 Insurance Premiums ($ per year)</h5>
                        <p className="text-sm text-gray-700 mb-2">Life/TPD/income protection inside super:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Typically $200-$1,000+/year depending on age and cover</li>
                            <li><strong>Near retirement?</strong> Consider cancelling insurance to preserve super</li>
                            <li>Review if you still need cover in retirement</li>
                            </ul>
                        </div>
                </div>
            )
        },
        {
            title: "Age Pension Calculation",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">How Age Pension Works:</h4>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">🎯 Two Tests Applied (Lower result applies)</h5>
                        <div className="space-y-3 mt-2">
                            <div className="bg-green-100 p-3 rounded">
                                <div className="font-semibold text-sm text-green-900 mb-1">1. Income Test</div>
                                <div className="text-xs text-green-800">
                                    • Pension reduces by 50c per $1 over income-free threshold<br/>
                                    • Income-free threshold ({pension.ratePeriodLabel}): {fmtAud(incomeFreeSingleFn, 0)}/fortnight (single), {fmtAud(incomeFreeCoupleFn, 0)}/fortnight (couple)<br/>
                                    • Includes super withdrawals, other income, deemed investment income
                                </div>
                            </div>
                            <div className="bg-green-100 p-3 rounded">
                                <div className="font-semibold text-sm text-green-900 mb-1">2. Assets Test</div>
                                <div className="text-xs text-green-800">
                                    • Pension reduces by $3/fortnight per $1,000 over asset-free threshold<br/>
                                    • Assets include: super, savings, investments, property (not home)<br/>
                                    • Different thresholds for homeowners vs non-homeowners
                                </div>
                            </div>
                        </div>
                    </div>

                        <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">💰 Maximum Age Pension ({pension.ratePeriodLabel})</h5>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-blue-100 p-2 rounded">
                                <div className="text-xs text-blue-700 font-semibold">Single</div>
                                <div className="text-lg font-bold text-blue-900">{fmtAud(pension.singleTotalAnnual)}</div>
                                <div className="text-xs text-blue-700">per year ({fmtAud(pension.singleTotalFn, 2)}/fn)</div>
                            </div>
                            <div className="bg-blue-100 p-2 rounded">
                                <div className="text-xs text-blue-700 font-semibold">Couple (combined)</div>
                                <div className="text-lg font-bold text-blue-900">{fmtAud(pension.coupleTotalAnnual)}</div>
                                <div className="text-xs text-blue-700">per year ({fmtAud(pension.coupleTotalFn, 2)}/fn)</div>
                            </div>
                        </div>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            Totals include Pension Supplement and Energy Supplement, matching the calculator rates file.
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">📊 Asset cut-offs — $0 Age Pension ({pension.assetEffectiveLabel} free areas; rates {pension.ratePeriodLabel})</h5>
                        <div className="text-sm text-yellow-800 space-y-1">
                            <div><strong>Single homeowner:</strong> {fmtAud(at.SINGLE_HOMEOWNER.CUT_OFF)}</div>
                            <div><strong>Single non-homeowner:</strong> {fmtAud(at.SINGLE_NON_HOMEOWNER.CUT_OFF)}</div>
                            <div><strong>Couple homeowner:</strong> {fmtAud(at.COUPLE_HOMEOWNER.CUT_OFF)}</div>
                            <div><strong>Couple non-homeowner:</strong> {fmtAud(at.COUPLE_NON_HOMEOWNER.CUT_OFF)}</div>
                        </div>
                        <div className="mt-2 p-2 bg-yellow-200 text-xs text-yellow-900 rounded">
                            At or above these asset levels = $0 pension under the assets test. Below = partial pension (income test may still reduce further).
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Understanding Results",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Interpreting Your Projection:</h4>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎯 Maximum Sustainable Income</h5>
                        <p className="text-sm text-purple-700 mb-2">The KEY number! This is the highest annual income you can safely withdraw such that:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li>Your super lasts for the selected retirement period</li>
                            <li>Adjusted for inflation (in today's dollars)</li>
                            <li>Includes Age Pension entitlement</li>
                            <li>Accounts for all fees and taxes</li>
                        </ul>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Example:</strong> If it shows $65,000, you can safely spend $65,000/year (today's purchasing power) throughout retirement
                        </div>
                        </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📈 Balance Over Time Chart</h5>
                        <p className="text-sm text-blue-700 mb-2">Shows your super balance year-by-year:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Declining line:</strong> Normal - you're drawing down savings</li>
                            <li><strong>Reaching $0:</strong> Money runs out - reduce spending or retire later</li>
                            <li><strong>Balance remains high:</strong> Could spend more or leave legacy</li>
                        </ul>
                        </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">💚 Income Composition Chart</h5>
                        <p className="text-sm text-green-700 mb-2">Shows where your income comes from:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Red:</strong> Super withdrawals</li>
                            <li><strong>Green:</strong> Age Pension</li>
                            <li><strong>Orange/Purple:</strong> Other income sources</li>
                        </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Tip:</strong> Watch pension increase as super decreases - they work together!
                        </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">📋 Year-by-Year Table</h5>
                        <p className="text-sm text-orange-700 mb-2">Detailed breakdown showing:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li>Your age each year</li>
                            <li>Super balance remaining</li>
                            <li>Annual super withdrawal</li>
                            <li>Age Pension received</li>
                            <li>Total income</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "What-If Scenarios",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Testing Different Situations:</h4>
                    <p className="text-sm text-gray-600">
                        These match the live Basic what-if controls (All Access). Preset return buttons (4.5% / 6.5% / 8.5%)
                        and early/typical/high scenario chips sit above the form — use those for quick baseline changes.
                    </p>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">📅 Live to Age</h5>
                        <p className="text-sm text-purple-700 mb-2">Extend or shorten the horizon to a target age:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li>Enter the age you want the plan to last until (e.g. 95 or 100)</li>
                            <li>See the revised maximum sustainable income and ending balance</li>
                            <li>Longer horizons usually mean a lower sustainable draw</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">💰 More Income</h5>
                        <p className="text-sm text-blue-700 mb-2">Add extra annual spending on top of the base plan:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li>Enter an extra dollar amount per year (e.g. $10,000)</li>
                            <li>Compare how long money lasts and the income trade-off vs the base case</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Use case:</strong> &quot;What if I want to spend $10,000 more per year?&quot;
                        </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">📊 Change Annual Fees (% pts)</h5>
                        <p className="text-sm text-orange-700 mb-2">Shift total fee drag up or down:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li>Enter a percentage-point change (e.g. −0.5 to model a cheaper fund)</li>
                            <li>Shows impact on sustainable income from fee compounding alone</li>
                        </ul>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">📉 Sequence Risk (% return for 3 yrs)</h5>
                        <p className="text-sm text-red-700 mb-2">Stress the first three retirement years:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li>Enter an assumed return for years 1–3 (e.g. −20)</li>
                            <li>Tests sequence-of-returns risk before markets recover</li>
                        </ul>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <h5 className="font-medium text-slate-800 mb-2">💑 Surviving Spouse (desktop, couples)</h5>
                        <p className="text-sm text-slate-700 mb-2">
                            On desktop for couple cases, run a surviving-spouse check to see how household income
                            and Age Pension settings change if one partner dies. Mobile focuses on the four core what-ifs above.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Tips & Best Practices",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Getting the Most from This Calculator:</h4>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">✅ Do These Things:</h5>
                        <ul className="list-disc list-inside text-green-700 text-sm space-y-2 ml-4">
                            <li><strong>Use conservative assumptions</strong> - Better to underestimate returns than overestimate</li>
                            <li><strong>Update annually</strong> - Re-run with actual balance and market conditions</li>
                            <li><strong>Test scenarios</strong> - Try Live to Age, More Income, Fees, Sequence Risk (and Surviving Spouse if a couple)</li>
                            <li><strong>Consider couple vs single</strong> - Relationship status significantly affects Age Pension</li>
                            <li><strong>Factor in homeownership</strong> - Makes huge difference to Age Pension eligibility</li>
                            <li><strong>Review fees</strong> - Even 0.5% difference compounds to huge $ over time</li>
                            </ul>
                        </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">❌ Common Mistakes:</h5>
                        <ul className="list-disc list-inside text-red-700 text-sm space-y-2 ml-4">
                            <li><strong>Using too optimistic returns</strong> - 10%+ is unrealistic for balanced portfolios</li>
                            <li><strong>Forgetting about fees</strong> - They compound and significantly reduce returns</li>
                            <li><strong>Ignoring inflation</strong> - $60k today ≠ $60k in 20 years</li>
                            <li><strong>Not testing scenarios</strong> - Markets fluctuate! Test different returns</li>
                            <li><strong>Assuming full Age Pension</strong> - Most retirees get partial or no pension</li>
                            </ul>
                        </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📊 Benchmarks to Consider:</h5>
                        <div className="text-sm text-blue-700 space-y-1">
                            <div><strong>Comfortable lifestyle (ASFA standard):</strong></div>
                            <div className="ml-4">• Single: ~$51,000/year</div>
                            <div className="ml-4">• Couple: ~$72,000/year</div>
                            <div className="mt-2"><strong>Modest lifestyle (ASFA standard):</strong></div>
                            <div className="ml-4">• Single: ~$32,000/year</div>
                            <div className="ml-4">• Couple: ~$46,000/year</div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">⚠️ When to Seek Professional Advice:</h5>
                        <ul className="list-disc list-inside text-yellow-700 text-sm space-y-1 ml-4">
                            <li>Complex financial situations (trusts, businesses, etc.)</li>
                            <li>Large super balances ($1M+) requiring tax planning</li>
                            <li>Transition to retirement (TTR) strategies</li>
                            <li>Centrelink/Age Pension optimization</li>
                            <li>Estate planning and death benefit nominations</li>
                            </ul>
                    </div>
                </div>
            )
        }
    ];

    return (
        <div ref={tutorialRef} className="space-y-6">
            {/* Progress Indicator */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-semibold text-gray-700">
                            Step {activeStep + 1} of {tutorialSteps.length}
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${((activeStep + 1) / tutorialSteps.length) * 100}%` }}
                        ></div>
                        </div>
                    </div>
                    </div>
                </div>

            {/* Current Step Content */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {tutorialSteps[activeStep].title}
                    </h3>
                <div className="prose max-w-none">
                    {tutorialSteps[activeStep].content}
                </div>
                </div>

            {/* Navigation Buttons */}
                <div className="flex justify-between items-center">
                    <button
                        onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                        disabled={activeStep === 0}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                    ← Previous
                    </button>
                <div className="flex gap-2">
                    {tutorialSteps.map((_, idx) => (
                            <button
                            key={idx}
                            onClick={() => setActiveStep(idx)}
                            className={`w-2 h-2 rounded-full transition ${
                                idx === activeStep ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                            />
                        ))}
                    </div>
                        <button
                            onClick={() => setActiveStep(Math.min(tutorialSteps.length - 1, activeStep + 1))}
                    disabled={activeStep === tutorialSteps.length - 1}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                    Next →
                        </button>
            </div>
        </div>
    );
};

export default BasicTutorialGuide;
